<template>
  <div>
    <header
      ref="topnav"
      :class="{
        'mainNavHidden': mainNavHidden,
        'ie11': isIE11,
        'search-active': searchActive,
        'close-search': closeSearch
      }"
      class="header-navigation-wrapper top-nav"
    >
      <a
        id="jump-to-header"
        href="#page-wrapper"
        class="button button--32 button--secondary skip-to-content"
      >
        Hoppa till sidans innehåll
      </a>
      <my-store
        :is-store="storeInformation.isStore"
        :store-data="storeData"
        :show-store="showStore"
        @update-store="updateSelectedStore"
      />
      <div class="main-nav">
        <div
          ref="wrapcontainer"
          class="wrap-container"
        >
          <div class="row-noGutter-middle-noWrap">
            <navigation
              ref="navwrapper"
              :menu-items="navBarItems"
              :is-store="storeInformation.isStore"
              :store-name="storeData && storeData.storeName"
              :profile="storeInformation.profile"
              :store-url="storeInformation && storeInformation.storeUrl"
              :inline-menu-overflown="mainNavHidden"
              :online-url="onlineUrlLocal"
              @initialWidth="initialWidth"
            />

            <div
              ref="separator"
              class="top-nav-separator"
            >
              <search-component
                v-if="renderSearch"
                ref="searchwrapper"
                :active="searchInitComplete"
                :style="searchInlineCssStyle"
                :class="{ 'is-initialized': searchInitComplete }"
                class="top-nav-search-component"
                @cancelsearchbar="cancelSearch"
              />
            </div>
            <div
              class="top-nav-right"
              :class="[renderClassNoEcommerce]"
            >
              <button
                type="button"
                aria-label="Sök på ica.se"
                aria-haspopup="true"
                class="button button--secondary button--32 button--icon button--search"
                @click="openSearch"
              >
                <svgicon
                  name="Navigation/Search"
                  class="search-icon"
                />
              </button>
              <avatar-dropdown
                :data="avatar"
                :store-data="storeData"
              />
              <a
                v-if="onlineUrlLocal"
                ref="onlineLink"
                :class="{
                  'button--secondary': !avatar.isLoggedIn,
                  'button--icon': showOnlyIconEcommerce,
                  'button--text-icon': !showOnlyIconEcommerce
                }"
                :href="onlineUrlLocal"
                class="button button--32 js-track-ecommerce ecommerce-link"
              >
                <span
                  v-if="(cart && cart.quantity) && showOnlyIconEcommerce"
                  class="ecommerce-link--ammount"
                >{{ cart.quantity }}</span>
                <svgicon name="Shop/ShoppingCart" />{{ onlineLinkText }}
              </a>
            </div>
          </div>
        </div>
      </div>
      <sub-navigation
        v-if="subNavBarItems.length"
        :menu-items="subNavBarItems"
      />

      <transition-group
        v-if="alertBanners.length"
        name="alert"
        tag="div"
        class="alert-banners-wrapper"
        @before-leave="beforeLeave"
        @after-leave="afterLeave"
        @leave="leave"
      >
        <alert-banner
          v-for="item in filteredAlerts"
          :key="item.id"
          ref="alert"
          v-bind="item"
          @close-alert="closeAlert"
        />
      </transition-group>
    </header>
  </div>
</template>

<script>
import throttle from 'lodash.throttle'
import debounce from "lodash.debounce"
import track from "@/analytics/dataLayerTopNav"
import myStore from "./subcomponents/my-store"
import Navigation from "./subcomponents/navigation"
import search from "./subcomponents/search/search"
import Avatar from "./subcomponents/avatar-dropdown"
import SubNavigation from "./subcomponents/sub-navigation"
import ResizeObserver from "@/utils/resizeObserver"
import IcaAlertBanner from "~/@ica/ica-molecules-vue/src/components/AlertBanner.vue"
import "#/Navigation/Search"
import "#/Shop/ShoppingCart"
import "#/Climate/ClimateGoals"
import "#/Time/WeeklyPlan"
import "#/Shop/ShoppingList"
import "#/Cooking/Recipe"
import "#/Controls/Settings"
import { setCookie, getCookie } from "@/utils/cookies.js"
import ICAOnlineSDK from './ICAOnlineSDK'
import ICAStoreSDK from './ICAStoreSDK'
import { mapActions } from 'vuex'
import { service } from '@/services/'

const DEFAULT_SEARCH_OFFSET = "100%"

export default {
  name: "TopNav",
  components: {
    "my-store": myStore,
    Navigation,
    "search-component": search,
    "avatar-dropdown": Avatar,
    "sub-navigation": SubNavigation,
    "alert-banner": IcaAlertBanner
  },
  props: {
    name: {
      type: String,
      default: ""
    },
    lastVisitedStore: {
      type: Object,
      default () {
        return null
      }
    },
    navBarItems: {
      type: Array,
      default () {
        return []
      }
    },
    subNavBarItems: {
      type: Array,
      default () {
        return []
      }
    },
    avatar: {
      type: Object,
      default () {
        return {}
      }
    },
    storeInformation: {
      type: Object,
      default () {
        return {}
      }
    },
    onlineUrl: {
      type: String,
      default: ""
    },
    alertBanners: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      // Header classes
      searchActive: false,
      isIE11: false,
      mainNavHidden: false, // If the nav list is hidden or not
      onlineUrlLocal: this.onlineUrl, // Just a data var of the prop
      renderSearch: false, // If the search box is visible or not
      searchInitComplete: false, // When search anim is complete
      inlineSearchLeftOffset: 0,
      currentLeftOffset: "0px", // The width the search box will be placed to the left och the nav list (i e next to the logo)
      closeSearch: false,
      initialNavigationWidth: null,
      showAlertBanners: this.alertBanners, // All alert banners (Same as prop alertBanners). Also see computed filteredfilteredAlerts.
      savedAlerts: [], // List with ids of alert banners closed by user
      alertCookieRead: false, // When the alert banner cookie is read(y)
      rafCount: 0, // Some magic count connected to requestAnimationFrame
      storeData: {},
      showStore: false,
      errors: [],
      onlineLinkText: 'E-handla',
      cart: null,
      cartAttempts: 0 // Attempts to get the icaOnlineobject from window.
    }
  },
  computed: {
    isMobileSearch () {
      return this.$root.currentSizeLte("md")
    },
    showOnlyIconEcommerce () {
      return (
        this.$root.currentSizeLte("xs") ||
        (this.$root.currentSizeLte("xs") && !this.avatar.isLoggedIn)
      )
    },
    smallSizeTracker () {
      return this.$root.currentSizeLte("sm")
    },
    searchInlineCssStyle () {
      let left = this.isMobileSearch ? "0" : this.currentLeftOffset
      return {
        left
      }
    },
    renderClassNoEcommerce () {
      return !this.onlineUrlLocal ? "no-ecommerce" : ""
    },
    /* filteredAlerts
     * Alerts that the user hasn't closed earlier and should be visible */
    filteredAlerts () {
      return this.showAlertBanners.filter(
        // Wait for alert cookie to prevent displaying already closed alerts */
        item => !this.savedAlerts.includes(item.id) && this.alertCookieRead
      )
    }
  },
  watch: {
    isMobileSearch (isMobileSearch) {
      this.updateTracking()
      this.updateTopPadding()
      if (isMobileSearch && this.searchActive) {
        document.documentElement.classList.add("no-scroll")
      } else if (!isMobileSearch && this.searchActive) {
        document.documentElement.classList.remove("no-scroll")
      }
    },
    showOnlyIconEcommerce () {
      this.updateTracking()
      this.updateTopPadding()
    },
    smallSizeTracker () {
      this.updateTracking()
      this.updateTopPadding()
    },
    mainNavHidden () {
      this.updateTracking()
      this.updateTopPadding()
    },
    storeData (newVal) {
      if (newVal && newVal.Name) {
        this.storeData.storeName = newVal.Name
      }
       this.showStore = true
     this.handleResize()
    },
    showStore () {
      this.handleResize()
    },
    cart (newCart) {
      if(newCart && newCart.total) {
        this.onlineLinkText = Number.isInteger(newCart.total) ? newCart.total + ' kr' : newCart.total.toFixed(2).replace('.', ',') + ' kr'
      }
      else {
        this.onlineLinkText = 'E-handla'
      }
    }
  },
  beforeDestroy () {
    ResizeObserver.removeAllCallbacks(this)
    window.removeEventListener("updateHeaderCart", this.updateCart)
    document.removeEventListener("toggleLoginModal", this.toggleLoginModal)
    document.removeEventListener('groceryBagStoreChange', this.handleStoreChangedEvent)
  },
  beforeMount () {
    if(this.$root.envConfig && this.$root.envConfig.onlineScriptSrc) {
      ICAOnlineSDK(this.$root.envConfig.onlineScriptSrc)
    }

    this.getAlertCookieValue()
  },
  mounted () {
    this.$root.isLoggedIn = this.avatar.isLoggedIn

    ResizeObserver.addCallback(this, this.handleResize)

    this.isIE11 = !!window.MSInputMethodContext && !!document.documentMode

    this.setStoreFromCookie()

    ICAStoreSDK.createOrUpdateICAStoreSDK(this.storeData)

    window.addEventListener("updateHeaderCart", this.updateCart)
    document.addEventListener('toggleLoginModal', this.toggleLoginModal)
    document.addEventListener('groceryBagStoreChange', this.handleStoreChangedEvent)
  },
  methods: {
    ...mapActions(['getPublicAccessToken']),
    toggleLoginModal (e) {
      if (this.$ICALogin && e.detail) {
        // Normally when using the molecules header you add the loginUrl via props.
        // However, if you use the ica-web-frontend topnav instead there is no such prop.
        if (!e.detail.loginUrl) {
          console.warn("Missing loginUrl in event detail")
        }
        this.$ICALogin.forceUserLogin(e.detail.loginUrl, () => {}, e.detail.options)
      } else {
        console.warn("this.$ICALogin not registred or missing event detail object")
      }
    },
    handleStoreChangedEvent (event) {
      const eventStoreId = this.prepareStoreId(event.detail?.store?.Id)
      if(this.storeData.storeId !== eventStoreId){
        this.updateSelectedStore(event.detail.store)
      }
    },
    /* updateTopPadding
     * Sets the body top padding to the same height the header has.
     * Adds alert banner height but not skip-to-content */
    updateTopPadding () {
      if (this.$refs.topnav) {
        this.$nextTick().then(() => {
          const height = Array.from(this.$refs.topnav.children).reduce(
            (sum, element) =>
              (sum +=
                element.id === "jump-to-header" ? 0 : element.offsetHeight),
            0
          )
          document.body.style.paddingTop = `${height}px`
        })
      }
    },
    /* dynamicHeightAlert
    /* Sets a height on each alert. Somehow needed to produce nice animations when closing them. */
    dynamicHeightAlert () {
      let alerts = this.$refs.alert

      if (alerts) {
        this.$nextTick().then(() => {
          alerts.forEach(alert => {
            let el = alert.$el
            el.style.height = "auto"
            el.style.height = `${el.scrollHeight}px`
          })
        })
      }
    },
    /* updatePaddingRaf, beforeLeave, afterLeave, leave
     * When closing alert banners we use requestAnimationFrame to simultaniously animate the body padding
     */
    updatePaddingRaf () {
      this.updateTopPadding()
      if (this.rafCount) {
        requestAnimationFrame(this.updatePaddingRaf)
      }
    },
    beforeLeave () {
      this.rafCount++
      this.updatePaddingRaf()
    },
    afterLeave () {
      this.rafCount--
      this.updateTopPadding()
    },
    leave (el) {
      el.style.height = "0"
    },
    /* initialWidth
     * Gets the initial width of the navigation list including logo and padding (from navigation.vue)
     * This is done to hide the nav list when to long to fit */
    initialWidth (width) {
      this.initialNavigationWidth = width
      this.handleResize()
    },
    /* handleResize
     * Fires on window resize (and some manual triggers) */
    handleResize: throttle(function () {
      this.updateTopPadding()
      this.dynamicHeightAlert()
      const navElement = this.$refs.navwrapper.$el // .nav-wrapper (logo and nav list)
      let availableSpaceWidth = navElement.scrollWidth + this.$refs.separator.offsetWidth // .nav-wrapper + .top-nav-separator (Available space)

      // If the width of the nav list exceeds available space, hide it.
      if (this.initialNavigationWidth > availableSpaceWidth) {
        this.mainNavHidden = true
      } else {
        this.mainNavHidden = false
      }

      // .nav-wrapper minus logo and padding
      this.inlineSearchLeftOffset = navElement.scrollWidth - navElement.children[0].scrollWidth

      if (this.currentLeftOffset !== DEFAULT_SEARCH_OFFSET) {
        this.currentLeftOffset = `-${this.inlineSearchLeftOffset}px`
      }
    }, 200),
    /* openSearch
     * Shows search box */
    openSearch () {
      track.openedSearch()
      this.handleResize()

      this.$nextTick(() => {
        this.renderSearch = true
        this.currentLeftOffset = DEFAULT_SEARCH_OFFSET

        setTimeout(() => {
          this.searchActive = true
          this.currentLeftOffset = `-${this.inlineSearchLeftOffset}px`
        }, 100)

        const searchInitCompleteDelay = this.isMobileSearch ? 0 : 500

        setTimeout(() => {
          this.searchInitComplete = true
        }, searchInitCompleteDelay)

        if (this.isMobileSearch) {
          this.$nextTick().then(() => {
            this.$refs.separator.querySelector("[type=search]").focus()
          })
          document.documentElement.classList.add("no-scroll")
        }
      })
    },
    cancelSearch () {
      this.closeSearch = true

      setTimeout(() => {
        this.currentLeftOffset = "100%"
        this.searchActive = false
        this.renderSearch = false
        this.searchInitComplete = false
        document.documentElement.classList.remove("no-scroll")
        this.closeSearch = false
      }, 200)
    },

    closeAlert (id) {
      this.savedAlerts.push(id)
      this.showAlertBanners = this.filteredAlerts
      setCookie("alert-banner-enabled", this.savedAlerts.join(","), 365)
    },
    /* getAlertCookieValue
     * The cookie "alert-banner-enable" holds an array with ids of alert banners the user has closed */
    getAlertCookieValue () {
      let cookieValue = getCookie("alert-banner-enabled")
      this.alertCookieRead = true

      if (cookieValue) {
        const ids = cookieValue.split(",")
        ids.forEach(item => {
          this.savedAlerts.push(item)
        })
      }
    },
    getStoreCookieValue () {
      const storeCookie = getCookie("store-cookie")
      return storeCookie ? JSON.parse(decodeURIComponent(storeCookie)) : null
    },
    /* setStoreFromCookie
       Is triggered on mount */
    setStoreFromCookie () {
      // If user has chosen a store earlier, store info is stored here
      const parsedStoreCookie = this.getStoreCookieValue()
      // If BHS (butikshemsida)
      if (this.storeInformation.isStore) {
        this.showStore = true
        // We need to fetch all info from store search since we haven't info about isOSP or account number
        this.setStoreByid({
          storeId: this.prepareStoreId(this.storeInformation.storeId),
          name: this.storeInformation.storeName,
          profile: this.storeInformation.profile,
          isOSP: false,
        })
      // No BHS but has a store cookie
      } else if (parsedStoreCookie) {
        this.setStoreByid(parsedStoreCookie)

      // No BHS and no store-cookie
      } else {
         // If user is logged but hasn't chosen a store we try to fetch users favorite stores and set the first
        if (this.avatar.isLoggedIn) {
          this.setFavoriteStore()
        } else {
          this.showStore = true
          track.selectedStore(false)
        }
      }
    },
    /* setFavoriteStore
       If user is logged in but hasn't chosen a store we try to fetch users favorite stores and set  */
    async setFavoriteStore () {
      try {
        const serviceResponse = await service.getFavoriteStores({
          url: this.$root.envConfig.customerStoresApiBaseUrl,
          publicAccessToken: await this.getPublicAccessToken()
        })

        if(serviceResponse.length > 0) {
          const data = serviceResponse[0]
          const useaccountnr = !!data.accountNumber // Cast to boolean
          let storeId = useaccountnr ? data.accountNumber : data.id

          this.setStoreByid({
            isOSP: useaccountnr,
            storeId,
            name: data.name
          })
        } else {
          this.showStore = true
          track.selectedStore(false)
        }
      } catch (error) {
        this.throwError(error)
        console.error("Could not get favorite stores", error)
      }
    },
    setStoreByid (_store) {
      const isOSP = !!_store.isOSP
      let storeId = _store.storeId

      fetch(`${this.$root.envConfig.baseUrl}/api/store/searchbyid?query=${storeId}&useaccountnr=${isOSP}`)
        .then(response => response.json())
        .then(
          data =>
            (data.Stats.TotalHits &&
              this.updateSelectedStore(data.Documents[0], true)) ||
            this.throwError("StoreId did not match a store")
        )
        .catch(err => this.throwError(err))
    },
    throwError (error) {
      this.errors.push(error)
      this.showStore = true
    },
    getOnlineSlug (onlineUrl) {
      let splittedUrl = onlineUrl.split("/")
      return splittedUrl[splittedUrl.indexOf("handla") + 1]
    },
    prepareStoreId (storeId) {
      // Always use a five digit number, ie 00394
      return storeId.length >= 5 ? storeId : "00000".substring(storeId.toString().length, 5) + storeId
    },
    /* UpdateSelectedStore
       @store - store search object
       @cookieStore - if store cookie exists
       Is triggered on user store change OR on mount when store is read from store cookie
       The store data is always fetched from the search api, either in store picker modal or from cookie store id */
    updateSelectedStore (store, cookieStore = false) {
      // Reset store and cart data
      this.cart = null
      this.storeData = {}
      let _onlineUrl = store.OnlineUrl // Url to shop, ie http://www.ica.se/handla/maxi-ica-stormarknad-hudiksvall-id_00394

      // To let the footer know which onlineurl to use we dispatch an custom event.
      // When we have an vuex store spanning all instances we kan use that instead.
      let onlineEvent = new CustomEvent('topNavStoreChange', {
        detail: {
          onlineUrl: _onlineUrl,
          store,
        }
      })
      document.dispatchEvent(onlineEvent)

      this.storeData.isOSP = store.OnlinePlatform === 'OSP' // OSP or ATG, different storeId is used to fetch data
      this.storeData.storeName = store.Name
      this.storeData.storeId = this.storeData.isOSP ? store.AccountNumber : this.prepareStoreId(store.Id.toString()) // Use "AccountNumber" if OSP otherwise "Id"
      this.storeData.LinkToWeb = store.Url || store.LinkToWeb // .Linktoweb is old search api, .Url is new.
      this.storeData.profile = store.ShortProfileName

      // Track store select
      track.selectedStore({
        id: store.Id.toString(),
        accountNumber: store.AccountNumber,
        name: store.Name,
        profile: this.storeData.profile
      })

      // If no .OnlineUrl exists on store object, use .Url of type 'e-handel' if it exists
      if (!_onlineUrl) {
        let urls = store.Urls.filter(link => link.Type == "e-handel")

        if (urls.length) {
          _onlineUrl = urls[0].Url
        }
      }

      this.storeData.onlineUrl = _onlineUrl

      // Set "E-handla" button link to store url if it exists, nothing if BHS, otherwise default shop page
      if (_onlineUrl) {
        this.onlineUrlLocal = _onlineUrl
      } else if (this.storeInformation.isStore) {
        this.onlineUrlLocal = ""
      } else {
        this.onlineUrlLocal = "https://handla.ica.se"
      }

      let storeSlug = ""
      // Create storeSeoUrl, ie "maxi-ica-stormarknad-hudiksvall-id_00394"
      if (cookieStore === false && _onlineUrl) {
        storeSlug = this.getOnlineSlug(_onlineUrl)
        this.setOnlineStore()
      }

      let cookieInfo = {
        storeName: store.Name,
        storeId: this.storeData.storeId,
        storeSeoUrl: storeSlug,
        isOSP: this.storeData.isOSP,
        profile: this.storeData.profile
      }

      // Get correct domain for cookie and then set store cookie
      const envConfig = this.$root.envConfig || this.$envConfig
      const cookieDomain =
        (envConfig && envConfig.cookieSubDomain) || ".ica.se"
      setCookie(
        "store-cookie",
        encodeURIComponent(JSON.stringify(cookieInfo)),
        365,
        cookieDomain
      )

      // Get cart info (shopping sum) for current store if logged in
      if (this.avatar.isLoggedIn && _onlineUrl !== null) {
        this.getCurrentCart(this.storeData.storeId)
      }

      ICAStoreSDK.createOrUpdateICAStoreSDK(this.storeData)
    },
    setNewStoreCookie () {
      let cookieInfo =  {
        storeName: this.storeData.storeName,
        storeId: this.storeData.storeId.toString(),
        storeSeoUrl: this.storeData.storeSeoUrl,
        isOSP: this.storeData.isOSP
      }
      setCookie("store-cookie", encodeURIComponent(JSON.stringify(cookieInfo)), 365,  this.$root.envConfig.cookieSubDomain)
    },
    setOnlineStore () {
      // Sets cookie for ATG-stores only
      if (!this.storeData.isOSP) {
        fetch(`/handla/browse/handleSelectStore.jsp?storeId=${this.storeData.storeId}`)
      }
    },
    getCurrentCart (storeId) {
      // Only for ATG stores.
      if (this.storeData.isOSP) {
        return
      }

      // The icaOnlineSDK sometimes takes a while before hooking up to the windows objects.
      // We do a maximum of five checks before giving up.
      if (typeof window !== "undefined" && window.icaOnline && storeId) {
        window.icaOnline.simpleCartApp
          .getCartByStore(storeId)
          .then(data => (this.cart = data))
          .catch(() => {
            this.cart = null
          })
      } else if (typeof window !== "undefined" && !window.icaOnline && this.cartAttempts < 6) {
        setTimeout(() => {
          this.cartAttempts++
          this.getCurrentCart(storeId)
        }, 500)
      }
    },
    updateCart () {
      this.getCurrentCart(this.storeData.storeId)
    },
    updateTracking: debounce(function () {
      if (!this.$data.mainNavHidden) {
        track.topNavLayout("x-large")
      } else if (!this.isMobileSearch) {
        track.topNavLayout("large")
      } else if (!this.smallSizeTracker) {
        track.topNavLayout("medium")
      } else if (!this.showOnlyIconEcommerce) {
        track.topNavLayout("small")
      } else {
        track.topNavLayout("x-small")
      }
    }, 50)
  }
}
</script>

<style lang="scss">
.top-nav {
  position: fixed;
  top: 0;
  right: 0;
  left: 0;
  z-index: 101;
  background-color: $color-white;

  @media print {
    display: none;
  }

  .skip-to-content {
    position: absolute;
    top: -999rem;
    left: 0.5rem;
    z-index: 4;

    &:focus {
      top: 0.5rem;
    }
  }

  .top-nav-search-component {
    position: absolute;
    top: -999em;
    opacity: 0;
    width: auto;

    .search-component {
      padding-left: 0;
      padding-right: 0;
    }
  }

  &.search-active {
    .main-nav > .wrap-container {
      padding: 0;

      @include media-breakpoint-up(lg) {
        padding-left: 1.5rem;
        padding-right: 1.5rem;
      }
    }

    .top-nav-search-component {
      display: block;
      height: 100vh;
      opacity: 1;
      position: fixed;
      top: 0;
      right: 0;
      padding-right: 0;

      @include media-breakpoint-up(lg) {
        height: auto;
        max-width: none;
        padding-left: 0.5rem;
        padding-right: 0.5rem;
        position: absolute;
        left: 100%;
        right: -2.5rem;
        top: -2rem;
        transition: left 200ms ease-in-out, opacity 400ms ease-in-out;
      }

      &.is-initialized {
        transition: none;
      }
    }

    &.close-search {
      .top-nav-search-component {
        @include media-breakpoint-up(lg) {
          transition: left 200ms ease-in-out, opacity 200ms ease-in-out;
          left: 100% !important;
          opacity: 0;
        }
      }
    }
  }

  .top-nav-separator {
    flex-grow: 1;
    position: relative;
  }
}

.main-nav {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0;
  position: relative;
  z-index: 3;
  background: $color-white;
  border-bottom: 0.0625rem solid $color-grey-special;
}

.top-nav-right {
  display: flex;
  justify-content: flex-end;
  padding: 1rem 0;
  position: relative;
  width: calc(100% - 7rem);
  align-items: center;

  @include media-breakpoint-up(md) {
    width: auto;
  }

  > :not(:last-child) {
    margin-right: 0.5rem;
  }

  .button {
    white-space: nowrap;

    &--search {
      user-select: none;
      -webkit-tap-highlight-color: transparent;
      border: 0.0625rem solid transparent;
      transition: all 200ms ease;

      .svg-icon {
        margin-top: -1px;
        margin-left: -1px;
      }

      .top-nav.search-active &,
      &.active {
        background-color: $color-white;
        border: 0.0625rem solid $color-grey-special;
        border-radius: 0.25rem;
        fill: $color-grey-special;
        height: 2.5rem;
        margin-bottom: -0.25rem;
        margin-top: -0.25rem;
        transition: all 200ms ease;
      }
    }

    &.ecommerce-link {
      position: relative;
      overflow: visible;

      .ecommerce-link--ammount {
        font-weight: 900;
        font-size: 0.75rem;
        line-height: 1;
        position: absolute;
        background: $color-red-dark;
        color: $color-white;
        border-radius: 50%;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        border: 0.125rem solid $color-white;
        width: 1.375rem;
        height: 1.375rem;
        z-index: 1;
        top: -0.625rem;
        right: -0.625rem;
      }
    }
  }

  &.no-ecommerce {
    .avatar-dropdown-placeholder {
      @include media-breakpoint-up(md) {
        left: auto;
        right: 0;
      }
    }
  }
}

.main-nav .nav-wrapper,
.main-nav .top-nav-right,
.my-store,
.subnav-desktop {
  .top-nav & {
    opacity: 1;
    transition: opacity 1.6s 0.1s;
  }

  .top-nav.search-active & {
    opacity: 0;

    /*
    visibility: hidden;
    position: absolute;
    top: -999999px;
    max-width: 100%;
    */

    @include media-breakpoint-up(lg) {
      position: relative;
      top: auto;
      opacity: 1;
      visibility: visible;
    }
  }
}

.alert-banners-wrapper {
  position: absolute;
  width: 100%;

  .alert-leave-active {
    transition: all 0.2s ease-in-out;
    transform: translateY(-2.8125rem);
  }
}
</style>
